import { useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router";
import { useGetEmployeeByIdQuery } from "../../../common/api/employee";
import { useGetNotificationsQuery } from "../../../common/api/notification";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useIsEstablishmentManager } from "../../../common/utils/useIsEstablishmentManager";
import notificationImage from "../../../common/images/notification.svg";
import { mapNotificationToItem } from "../../../common/utils/mapNotificationToItem";
import {
    setIsCreateCertificateModalOpen,
    setSelectedEmployee as CertificateSetSelectedEmployee,
} from "../../../modals/CreateCertificateModal/CreateCertificateModalSlice";

type UseEmployeeDetailSidebarResult = {
    isLoading: boolean;
    shouldRender: boolean;
    notifications?: {
        head: { title: string; iconUrl: string };
        items: ReturnType<typeof mapNotificationToItem>;
    };
    onAddCertificate: () => void;
};

export const useEmployeeDetailSidebar = (): UseEmployeeDetailSidebarResult => {
    const employeeId = Number(useParams().employeeId);
    const dispatch = useAppDispatch();
    const isEstablishmentManager = useIsEstablishmentManager();

    const { isPostCreateCertificateSuccess } = useAppSelector(
        (state) => state.createCertificateModal,
    );
    const { isPatchEditEmployeeSuccess } = useAppSelector(
        (state) => state.editEmployeeModal,
    );

    const { data: employeeData, isLoading: employeeDataLoading } =
        useGetEmployeeByIdQuery({ id: employeeId });

    const {
        data: employeeNotifications,
        refetch: refetchEmployeeNotifications,
    } = useGetNotificationsQuery({
        filter: { employee_id: employeeId },
    });

    useEffect(() => {
        if (isPatchEditEmployeeSuccess || isPostCreateCertificateSuccess) {
            refetchEmployeeNotifications();
        }
    }, [
        isPatchEditEmployeeSuccess,
        isPostCreateCertificateSuccess,
        refetchEmployeeNotifications,
    ]);

    const onAddCertificate = useCallback(() => {
        if (!employeeData) return;
        dispatch(setIsCreateCertificateModalOpen(true));
        dispatch(CertificateSetSelectedEmployee(employeeData));
    }, [dispatch, employeeData]);

    const notifications = useMemo(() => {
        if (!employeeNotifications?.data) return undefined;
        return {
            head: {
                title: "Belangrijke meldingen",
                iconUrl: notificationImage,
            },
            items: mapNotificationToItem(employeeNotifications.data, 4),
        } as const;
    }, [employeeNotifications]);

    const shouldRender = Boolean(
        isEstablishmentManager &&
        employeeData &&
        employeeNotifications?.data?.length,
    );

    return {
        isLoading: employeeDataLoading,
        shouldRender,
        notifications,
        onAddCertificate,
    };
};
